// CSV export helpers (2026-10-26). Used by the Transaction Pending cards'
// "Export CSV" button. Plain browser code - no new npm dependency.

// One cell. Null/undefined become empty; objects are JSON (the Java Pending
// record is a raw Redis value whose shape was never confirmed, so nested
// fields can turn up). Text starting with = + - @ gets a leading apostrophe
// so Excel/Sheets read it as text, not a formula - except plain numbers like
// "-100.00", which must stay numbers.
export function csvCell(value) {
  if (value === null || value === undefined) {
    return '';
  }
  let text;
  if (typeof value === 'object') {
    text = JSON.stringify(value);
  } else {
    text = String(value);
  }
  if (/^[=+\-@\t\r]/.test(text) && !/^[+-]?\d+(\.\d+)?$/.test(text)) {
    text = `'${text}`;
  }
  if (/[",\r\n]/.test(text)) {
    text = `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

// columns: [{ header, value: (row) => any }]
export function toCsv(columns, rows) {
  const lines = [columns.map((c) => csvCell(c.header)).join(',')];
  for (const row of rows) {
    lines.push(columns.map((c) => csvCell(c.value(row))).join(','));
  }
  return lines.join('\r\n') + '\r\n';
}

// UTF-8 BOM so Excel opens merchant names with non-ASCII characters correctly.
export function downloadCsv(filename, text) {
  const blob = new Blob(['﻿', text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Local time, file-name safe: 20261026-210233
export function fileTimestamp(date = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}${p(date.getMonth() + 1)}${p(date.getDate())}-${p(date.getHours())}${p(date.getMinutes())}${p(date.getSeconds())}`;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Fetches every page of a paginated pending endpoint and returns all rows.
//
// fetchPage(page, perPage) -> the endpoint's normal JSON ({transactions,
// total, generated_at?, building?}). The API caps per_page at 100, so a large
// list takes several requests; they run a few at a time.
//
// The two Redis-scan cards (awaiting / no Leg 3) page through a cached scan.
// If that scan is replaced mid-export (generated_at changes), pages would mix
// two snapshots, so the export starts over (up to maxRestarts). Rows are also
// de-duplicated by keyOf, which covers the live Java/PHP sources shifting by a
// row between requests.
export async function fetchAllPages(fetchPage, {
  perPage = 100,
  concurrency = 4,
  keyOf = null,
  onProgress = () => {},
  buildingRetryMs = 4000,
  buildingMaxRetries = 45,
  maxRestarts = 2,
} = {}) {
  for (let attempt = 0; attempt <= maxRestarts; attempt += 1) {
    let first;
    for (let tries = 0; ; tries += 1) {
      first = await fetchPage(1, perPage);
      if (!first?.building) break;
      if (tries >= buildingMaxRetries) {
        throw new Error('The Redis scan is taking too long. Try again in a minute.');
      }
      onProgress({ done: 0, total: 0, building: true });
      await sleep(buildingRetryMs);
    }

    const totalPages = Math.max(Math.ceil((first.total || 0) / perPage), 1);
    const snapshot = first.generated_at ?? null;
    const pages = new Array(totalPages);
    pages[0] = first.transactions || [];
    let done = 1;
    onProgress({ done, total: totalPages });

    let next = 2;
    let restart = false;
    const worker = async () => {
      while (!restart && next <= totalPages) {
        const page = next;
        next += 1;
        const data = await fetchPage(page, perPage);
        if (data?.building || (snapshot !== null && (data?.generated_at ?? null) !== snapshot)) {
          restart = true;
          return;
        }
        pages[page - 1] = data?.transactions || [];
        done += 1;
        onProgress({ done, total: totalPages });
      }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, totalPages - 1) }, worker));
    if (restart) {
      continue;
    }

    const rows = [];
    const seen = new Set();
    for (const list of pages) {
      for (const row of list || []) {
        const key = keyOf ? keyOf(row) : null;
        if (key) {
          if (seen.has(key)) continue;
          seen.add(key);
        }
        rows.push(row);
      }
    }
    return { rows, generatedAt: snapshot };
  }
  throw new Error('The list kept changing while exporting. Try again.');
}
